import axios, { CancelToken, isCancel } from 'axios';
import get from 'lodash.get';
import { Observable, from } from 'rxjs';

import { MediaType } from '../../enums/media-type';
import { mapProductsFiltersToRequestBody } from '../../services/filtering/api-param-map-service';
import { PAGE_SIZE_250, FIRST_PAGE } from '../../services/pagination-service';
import { createCancelablePromise } from '../../services/promises/create-cancelable-promise';

export const getProductSearchResults = (searchTerm) => {
  return new Observable((observer) => {
    const cancelToken = CancelToken.source();
    axios
      .get(`/product/autocomplete/${encodeURIComponent(searchTerm)}`, {
        cancelToken: cancelToken.token,
      })
      .then((response) => {
        observer.next(get(response, 'data', undefined));
        observer.complete();
      })
      .catch((e) => {
        if (isCancel(e)) {
          observer.complete();
        }
        observer.error(e);
      });
    return () => cancelToken.cancel();
  });
};

export const bulkLoadProducts = (productIds) => axios.post('/product/bulkLoad', { productIds }).then((resp) => get(resp, 'data', []));

export const bulkLoadProductsAsObservable = (productIds) => from(bulkLoadProducts(productIds));

export const getProductProfiles = () => {
  const request = axios.get('/product/profiles').then((response) => {
    return get(response, 'data', undefined);
  });

  const data$ = from(request);
  return data$;
};

export const createProduct = (product, updateOptions) => {
  const updatePayload = {
    ...product,
    ...updateOptions,
  };

  const request = axios.post('product', updatePayload).then((response) => get(response, 'data', undefined));

  const data$ = from(request);
  return data$;
};

export const updateProduct = (product, updateOptions) => {
  const { productId } = product;
  const updatePayload = {
    ...product,
    ...updateOptions,
  };

  const request = axios.put(`product/update/${productId}`, updatePayload).then((response) => get(response, 'data', undefined));

  const data$ = from(request);
  return data$;
};

export const getProductCategories = () => {
  return axios.get('productCategory').then((response) => {
    return get(response, 'data', undefined);
  });
};

export const getProductCategoriesAsObservable = () => {
  const request = getProductCategories();
  const data$ = from(request);
  return data$;
};

export const getProductActiveShippingLocationsPromise = () => {
  return axios.get('/product/activeShippingLocations');
};

export const saveShippingDefaults = (props) => {
  const {
    productId,
    sellerLabelProviderId,
    serviceId,
    packageTypeId,
    confirmationId,
    weight,
    weightUnitOfMeasureId,
    length,
    width,
    height,
    dimensionUnitOfMeasureId,
    isInternational,
    serviceCollectionGuid,
  } = props;

  const rateShopperField = isInternational ? 'IntlRateShopperGuid' : 'DomesticRateShopperGuid';
  const serviceFields = serviceCollectionGuid
    ? { [rateShopperField]: serviceCollectionGuid }
    : { SellerLabelProviderId: sellerLabelProviderId, ServiceId: serviceId };

  const shippingDefaults = {
    PackageTypeId: packageTypeId,
    ConfirmationId: confirmationId.toString(),
    Weight: weight,
    WeightUnitOfMeasureId: weightUnitOfMeasureId,
    Length: length,
    Width: width,
    Height: height,
    DimensionUnitOfMeasureId: dimensionUnitOfMeasureId,
    IsInternational: isInternational,
    ...serviceFields,
  };

  return axios.put(`product/shippingdefaults/${productId}`, shippingDefaults).then((response) => {
    return get(response, 'data', undefined);
  });
};

const productFilterBuilder = (searchTerm = '', appliedFilters) => {
  const filtersObj = mapProductsFiltersToRequestBody(appliedFilters);
  if (searchTerm) {
    filtersObj.searchTerm = searchTerm;
  }
  return filtersObj;
};

export const getProducts = ({
  pageNumber = FIRST_PAGE,
  pageSize = PAGE_SIZE_250,
  orderBy = 'CreateDate',
  direction = 'Ascending',
  searchTerm = '',
  appliedFilters,
}) => {
  const params = {
    page: {
      pageNumber,
      pageSize,
    },
    orderBy,
    direction,
  };
  const filter = {};
  if (searchTerm) {
    filter.searchTerm = searchTerm;
  }
  const getAndTransform = axios
    .post('/productgrid', {
      ...params,
      filter: productFilterBuilder(searchTerm, appliedFilters),
    })
    .then((data) => {
      return get(data, 'data', {});
    });

  return createCancelablePromise(getAndTransform);
};

export const deleteProduct = (payload) => {
  const getAndTransform = axios.delete('/product', { data: payload });
  return createCancelablePromise(getAndTransform);
};

export const getReportingCategories = (pageNumber = FIRST_PAGE, pageSize = PAGE_SIZE_250, orderBy = 'name', direction = 'descending') => {
  const getAndTransform = axios.post('/productCategoryGrid', {
    orderBy,
    direction,
    page: {
      pageNumber,
      pageSize,
    },
  });

  return createCancelablePromise(getAndTransform);
};

export const addReportingCategory = (name) => {
  const getAndTransform = axios.post('/productCategory', {
    name,
    active: true,
  });
  return createCancelablePromise(getAndTransform);
};

export const updateReportingCategory = (categoryId, name) => {
  const getAndTransform = axios.put(`/productCategory/${categoryId}`, {
    categoryId,
    name,
    active: true,
  });
  return createCancelablePromise(getAndTransform);
};

export const deleteReportingCategory = (categoryId) => {
  const getAndTransform = axios.delete(`/productCategory/${categoryId}`);
  return createCancelablePromise(getAndTransform);
};

export const getProductActivity = (productId, page, pageSize) => {
  const request = axios.get(`product/activity/${productId}?page=${page}&pageSize=${pageSize}`).then((response) => {
    return get(response, 'data', undefined);
  });

  return request;
};
export const getPresetGroups = (pageNumber = FIRST_PAGE, pageSize = PAGE_SIZE_250, orderBy = 'name', direction = 'descending') => {
  const getAndTransform = axios.post('/productProfileGrid', {
    orderBy,
    direction,
    page: {
      pageNumber,
      pageSize,
    },
  });

  return createCancelablePromise(getAndTransform);
};

export const addPresetGroup = (presetGroupPayload) => {
  const getAndTransform = axios.post('/productProfile', presetGroupPayload);
  return createCancelablePromise(getAndTransform);
};

export const updatePresetGroup = (profile, updateOptions) => {
  const { productProfileId } = profile;
  const updatePayload = {
    ...profile,
    ...updateOptions,
  };
  const getAndTransform = axios.put(`/productProfile/${productProfileId}`, updatePayload);
  return createCancelablePromise(getAndTransform);
};

export const deletePresetGroup = (profileId) => {
  const getAndTransform = axios.delete(`/productProfile/${profileId}`);
  return createCancelablePromise(getAndTransform);
};

export const addCategoryToProducts = (products, categoryId) => {
  const payload = products.map((product) => ({
    ...product,
    categoryId,
    isActive: product.isActive || product.active,
  }));
  const getAndTransform = axios.post('/product/bulkUpdate', payload);
  return createCancelablePromise(getAndTransform);
};

export const addPresetGroupToProducts = (products, productProfileId) => {
  const payload = products.map((product) => ({
    ...product,
    productProfileId,
    isActive: product.isActive || product.active,
  }));
  const getAndTransform = axios.post('/product/bulkUpdate', payload).then(({ data }) => data);
  return createCancelablePromise(getAndTransform);
};

export const deactivateProducts = (products) => {
  const payload = products.map((product) => ({ ...product, isActive: false }));
  const getAndTransform = axios.post('/product/bulkUpdate', payload);
  return createCancelablePromise(getAndTransform);
};

/**
 * Bulk update multiple products
 * @param {object[]} products - product entities to update.
 * NOTE: V3API BulkLoad Products returns 'active' (and thus what is in FE entities) while the update endpoint expects 'isActive'.
 * You'll notice that discrepancy in the above functions as well
 * @returns {Promise<object>}
 */
export const bulkUpdateProductGeneric = async (products) => {
  const productsUpdateRequest = products.map((product) => ({
    ...product,
    isActive: product.isActive || product.active,
  }));
  return axios.post('/product/bulkUpdate', productsUpdateRequest).then((response) => get(response, 'data', undefined));
};

export const addProductWarehouse = ({ productIds, warehouseId }) => {
  const request = axios.post('/product/addWarehouse', { warehouseId, productIds }).then((data) => data);
  return from(request);
};

export const removeProductWarehouse = ({ productIds, warehouseId }) => {
  const request = axios.post('/product/removeWarehouse', { warehouseId, productIds }).then((data) => data);
  return from(request);
};

/**
 * @typedef ProductWarehouseAssociationRequest
 * @property {number[]} productIds
 * @property {number[]} warehouseIdsToAdd
 * @property {number[]} warehouseIdsToDelete
 */

/**
 * @param {ProductWarehouseAssociationRequest} bulkRequest
 * @returns {Promise<AxiosResponse<object>>}
 */
export const bulkUpdateProductWarehouseAssociations = (bulkRequest) => {
  const request = axios.post('/product/updateWarehouses', bulkRequest).then((data) => data);
  return from(request);
};

export const addProductTag = ({ productIds, tagId }) => {
  const request = axios.post('/product/addTag', { productIds, tagId }).then((data) => data);
  return from(request);
};

export const removeProductTag = ({ productIds, tagId }) => {
  const request = axios
    .post('/product/removeTag', {
      productIds,
      tagId,
    })
    .then((data) => data);
  return from(request);
};

export const startProductImport = ({ file, identifyBy, overwriteExisting, importType }) => {
  const formData = new FormData();
  formData.append('CsvFile', file);
  formData.append('ImportType', importType);
  formData.append('IdentifyBy', identifyBy);
  formData.append('OverwriteExisting', overwriteExisting);
  formData.append('ShouldUpdateAllStores', true);

  const request = axios
    .post('/productgrid/import', formData, {
      headers: {
        ContentType: 'multipart/form-data',
      },
    })
    .then((response) => {
      return response?.data;
    });

  return from(request);
};

export const getImportLogFromJobGUID = (jobGuid) => {
  const getAndTransform = axios.get(`/productgrid/importLog?jobGuid=${jobGuid}`).then((response) => response.data);
  return from(getAndTransform);
};

export const startProductExport = (searchTerm = '', appliedFilters, productIds) =>
  from(
    axios
      .post('/productgrid/export', {
        filter: productFilterBuilder(searchTerm, appliedFilters),
        productIds,
      })
      .then((resp) => get(resp, 'data', [])),
  );

export const combineProducts = (masterProductId, childProductIds) => {
  const getAndTransform = axios.post('/product/combine', {
    masterProductId,
    childProductIds,
  });
  return createCancelablePromise(getAndTransform);
};

export const combineVariants = (parentProductData, childProductIds) => {
  const getAndTransform = axios.post('/product/combineVariants', {
    ...parentProductData,
    childProductIds,
  });
  return createCancelablePromise(getAndTransform);
};

export const addProductVariant = (parentProductId, productId, sku, name) => {
  const postAndTransform = axios.post('/product/productVariant', {
    parentProductId,
    productId,
    sku,
    name,
  });
  return createCancelablePromise(postAndTransform);
};

export const removeProductVariant = (productVariantId) => {
  const postAndTransform = axios.put(`/product/promoteToStandalone/${productVariantId}`);
  return createCancelablePromise(postAndTransform);
};

export const getUseConsolidatedInventory = () => {
  const getAndTransform = axios.get('/consolidatedInventory/hasConnections').then((response) => response.data);
  return createCancelablePromise(getAndTransform);
};

export const bulkUpdateAutoSplitOption = (products, autoSplitOption, autoSplitCustomQuantity) => {
  const payload = {
    autoSplitOption,
    autoSplitCustomQuantity,
    productIds: products.map(({ productId }) => productId),
  };
  const postAndTransform = axios.post('/product/bulkUpdateAsync', payload).then((data) => data);
  return createCancelablePromise(postAndTransform);
};

/**
 * @param {string} productId
 * @param {string} documentType
 * @param {File} file
 * @returns {Promise}
 */
export const uploadProductDocument = (productId, documentType, file) => {
  const formData = new FormData();
  formData.append('DocumentType', documentType);
  formData.append('File', file);

  return axios.post(`/product/${productId}/documents`, formData, {
    headers: {
      ContentType: MediaType.MultipartFormData,
    },
  });
};

/**
 * @param {string} productId
 * @param {string} documentType
 * @returns {Promise}
 */
export const downloadProductDocument = (productId, documentType) => {
  return axios.get(`/product/${productId}/documents/${documentType}`, {
    responseType: 'blob',
  });
};

/**
 * @param {string} productId
 * @param {string} documentType
 * @returns {Promise}
 */
export const deleteProductDocument = (productId, documentType) => {
  return axios.delete(`/product/${productId}/documents/${documentType}`);
};
